import { type Component, For } from "solid-js";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Granular skeleton for the main-content region (dashboard cards + task-list
 * rows). Mirrors the inner content of {@link WorkspaceSkeleton} so it can be
 * used as a Suspense fallback inside the already-mounted app shell.
 */
export const ContentSkeleton: Component = () => (
  <div class="flex-1 overflow-hidden p-6 space-y-6 select-none pointer-events-none">
    {/* Dashboard cards placeholder */}
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <For each={Array.from({ length: 3 })}>
        {() => (
          <div class="p-5 border border-border/30 bg-card/5 rounded-xl space-y-3">
            <div class="flex items-center justify-between">
              <Skeleton class="h-4 w-24 bg-muted/30 rounded" />
              <Skeleton class="h-3 w-10 bg-muted/20 rounded" />
            </div>
            <div class="space-y-1.5 pt-1">
              <Skeleton class="h-2.5 w-full bg-muted/15 rounded" />
              <Skeleton class="h-2.5 w-[75%] bg-muted/15 rounded" />
            </div>
          </div>
        )}
      </For>
    </div>

    {/* Table / List layout placeholder */}
    <div class="space-y-4 pt-2">
      <div class="flex items-center justify-between">
        <Skeleton class="h-5 w-24 bg-muted/35 rounded" />
        <Skeleton class="h-7 w-16 bg-muted/20 rounded" />
      </div>

      {/* Task list rows skeleton */}
      <div class="border border-border/30 bg-card/5 rounded-xl divide-y divide-border/20">
        <For each={Array.from({ length: 4 })}>
          {() => (
            <div class="flex items-center gap-4 p-4">
              <Skeleton class="h-4.5 w-4.5 rounded-full bg-muted/30 shrink-0" />
              <div class="flex-1 space-y-1.5">
                <Skeleton class="h-3.5 w-1/3 bg-muted/25 rounded" />
                <Skeleton class="h-2.5 w-1/5 bg-muted/15 rounded" />
              </div>
              <Skeleton class="h-3 w-12 bg-muted/20 rounded" />
            </div>
          )}
        </For>
      </div>
    </div>
  </div>
);

export const WorkspaceSkeleton: Component = () => (
  <div class="flex h-dvh w-full text-foreground overflow-hidden bg-background/50 select-none pointer-events-none">
    {/* Sidebar Skeleton (desktop only) */}
    <div class="hidden lg:flex flex-col w-64 border-r border-border/40 bg-card/10 shrink-0 p-4 gap-6 pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      {/* Logo area */}
      <div class="flex h-12 items-center px-2 shrink-0">
        <Skeleton class="h-7 w-28 bg-muted/30" />
      </div>

      {/* Smart Views / Nav Items */}
      <div class="flex flex-col gap-2.5 flex-1">
        <For each={Array.from({ length: 6 })}>
          {() => (
            <div class="flex items-center gap-3 px-3 py-2 rounded-md bg-accent/5">
              <Skeleton class="h-4 w-4 rounded bg-muted/30 shrink-0" />
              <Skeleton class="h-3.5 w-24 bg-muted/20" />
              <div class="flex-1" />
              <Skeleton class="h-3 w-5 rounded bg-muted/15" />
            </div>
          )}
        </For>
      </div>

      {/* Bottom profile / debug */}
      <div class="flex flex-col gap-2.5 shrink-0 pt-4 border-t border-border/30">
        <div class="flex items-center gap-3 px-3 py-2">
          <Skeleton class="h-4.5 w-4.5 rounded-full bg-muted/30 shrink-0" />
          <Skeleton class="h-3.5 w-16 bg-muted/25" />
        </div>
        <div class="flex items-center gap-3 px-3 py-2">
          <Skeleton class="h-4.5 w-4.5 rounded-full bg-muted/30 shrink-0" />
          <Skeleton class="h-3.5 w-16 bg-muted/25" />
        </div>
      </div>
    </div>

    {/* Main content Skeleton */}
    <div class="flex-1 flex flex-col min-w-0 h-full pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      {/* Header Skeleton */}
      <header class="flex h-16 items-center justify-between px-6 border-b border-border/40 bg-card/5 shrink-0">
        <div class="flex items-center gap-4">
          {/* Menu Trigger (mobile only) */}
          <Skeleton class="lg:hidden h-8 w-8 rounded-md bg-muted/30" />
          <Skeleton class="h-5 w-28 bg-muted/35 rounded" />
        </div>
        <div class="flex items-center gap-3">
          <Skeleton class="h-7 w-20 bg-muted/20 rounded-full" />
          <Skeleton class="h-8 w-8 rounded-full bg-muted/30" />
        </div>
      </header>

      {/* Content list / Dashboard layout */}
      <ContentSkeleton />
    </div>
  </div>
);
